import * as Yup from "yup";
import {
  ALPHABET_NAME_REGEX,
  COMPANY_NAME_REGEX,
  EMAIL_REGEX,
  STRONG_PASSWORD,
  UAE_MOBILE_REGEX,
  IN_MOBILE_REGEX,
  WEBSITE_LINK_REGEX,
} from "./regex";
import { isProd } from "../services/_apiConfig/apiConfig";

const STRONG_PASSWORD_MESSAGE =
  "Password must contain at least one uppercase, one lowercase, one number, and one special character";

const emailField = (maxLength) =>
  Yup.string()
    .transform((value, originalValue) => {
      const src =
        originalValue !== undefined && originalValue !== null
          ? originalValue
          : value;
      if (typeof src !== "string") return src;
      return src.trim().toLowerCase();
    })
    .max(maxLength, "Email is too long")
    .matches(EMAIL_REGEX, "Please enter a valid email address")
    .required("Please enter your email");

const strongTrimmedPassword = () =>
  Yup.string()
    .trim()
    .min(6, "Minimum 6 characters required")
    .matches(STRONG_PASSWORD, STRONG_PASSWORD_MESSAGE);

const phoneNumberSchema = Yup.string()
  .required("Mobile number is required")
  .test(
    "valid-mobile-based-on-env",
    isProd
      ? "Only valid UAE mobile numbers are allowed (e.g. 501234567)"
      : "Enter a valid UAE or Indian mobile number",
    (value) => {
      const number = value || "";
      if (isProd) {
        return UAE_MOBILE_REGEX.test(number);
      }
      return UAE_MOBILE_REGEX.test(number) || IN_MOBILE_REGEX.test(number);
    }
  );

export const loginSchema = Yup.object().shape({
  email: emailField(254),
  password: Yup.string().required("Please enter your password"),
});

export const forgotPasswordSchema = Yup.object().shape({
  email: emailField(254),
});

export const signupSchema = Yup.object().shape({
  company_name: Yup.string()
    .trim()
    .min(3, "Company name is too short")
    .max(100, "Company name is too long")
    .matches(COMPANY_NAME_REGEX, "Company name has invalid characters")
    .required("Please enter your company name"),
  company_website: Yup.string()
    .trim()
    .max(100, "Website URL is too long")
    .test(
      "valid-url-or-empty",
      "Please enter a valid website",
      (value) => !value || WEBSITE_LINK_REGEX.test(value)
    ),
  email: emailField(254),
  first_name: Yup.string()
    .min(2, "First name is too short")
    .max(50, "First name is too long")
    .matches(ALPHABET_NAME_REGEX, "Must contain only alphabets")
    .required("Please enter your first name"),

  last_name: Yup.string()
    .min(1, "Last name is too short")
    .max(50, "Last name is too long")
    .matches(ALPHABET_NAME_REGEX, "Must contain only alphabets")
    .required("Please enter your last name"),
  company_type: Yup.mixed().required("Please select a company type"),
  phone_number: phoneNumberSchema,
  user_type: Yup.string().required("Required"),
  password: strongTrimmedPassword().required("Please enter your password"),
  accept_tnc: Yup.boolean().oneOf([true], "Please accept terms"),
  accept_nda: Yup.boolean().oneOf([true], "Please accept non disclosure agreement"),
});

export const verifyPhoneSchema = Yup.object().shape({
  mobile_code: Yup.string()
    .length(4, "Please enter the 4-digit OTP")
    .required("Please enter OTP"),
});

export const changePasswordSchema = Yup.object().shape({
  old_password: Yup.string()
    .min(6, "Minimum 6 characters required!")
    .required("Please enter your current password"),

  new_password: strongTrimmedPassword()
    .test(
      "not-same-as-old",
      "New password cannot be the same as old password",
      function (value) {
        const { old_password } = this.parent;
        return !value || !old_password || value !== old_password;
      }
    )
    .required("Please enter your new password"),

  confirm_password: Yup.string()
    .min(6, "Minimum 6 characters required")
    .required("Please confirm your password")
    .oneOf([Yup.ref("new_password")], "Passwords do not match"),
});

// Back-compat: old export name had a typo
export const chnagePasswordSchema = changePasswordSchema;

export const emailSchema = Yup.object().shape({
  phone_number: phoneNumberSchema,
});

export const resetPasswordSchema = Yup.object().shape({
  code: Yup.string()
    .length(4, "Please enter the 4-digit OTP")
    .required("Please enter email OTP"),
  password: strongTrimmedPassword().required("Please enter your password"),
  confirmPassword: Yup.string()
    .min(6, "Minimum 6 characters required")
    .required("Please confirm your password")
    .oneOf([Yup.ref("password")], "Passwords do not match"),
});

export const bankAccountSchema = Yup.object().shape({
  account_holder_name: Yup.string().required("Please enter Account Holder Name"),
  city: Yup.object()
    .shape({
      value: Yup.string().required("Please select city"),
    })
    .required("Please select city"),
  bank: Yup.object()
    .shape({
      value: Yup.string().required("Please select bank"),
    })
    .required("Please select bank"),
  account_number: Yup.string()
    .matches(
      /^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/,
      "Please enter a valid IBAN"
    )
    .required("Please enter IBAN"),
  swift_code: Yup.string()
    .matches(
      /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
      "Please enter a valid SWIFT Code"
    )
    .required("Please enter Swift Code"),
});
